import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/common/components/ui/button";
import { Badge } from "@/projects/FinEaseHomeAndAdmin/components/ui/badge";
import { Users, RefreshCw, Download, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TableColumnsType } from "antd";
import { AntDTable } from "@/common/components/ui";
import IPOService from "@/services/IPO/APIs/IPOService";
import { GetMembers } from "@/utils/types/IPO/ipo";
import { notify } from "@/common/components/common/Notification";
import DeleteConfirmationModal from "@/projects/FinEaseHomeAndAdmin/components/DeleteConfirmationModal";
import Loader from "@/common/components/ui/loader";
import * as XLSX from "xlsx";

const MemberManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, _setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<GetMembers[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<GetMembers[]>([]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await IPOService.getMemberListAdminList();
      setMembers(response.results || []);
      return true;
    } catch (error) {
      console.error("Failed to fetch members:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const syncMembers = async () => {
    const ok = await fetchMembers();
    if (ok) {
      notify.success("", "Member data synced successfully.");
    } else {
      notify.error("", "Failed to sync member data.");
    }
  };

  // const handleDeleteClick = (id: string, name: string) => {
  //   setMemberToDelete({ id, name });
  //   setDeleteModalOpen(true);
  // };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete) {
      return;
    }

    try {
      setActionLoading(true);
      const res: any = await IPOService.deleteMember(memberToDelete.id);
      notify.success(
        "",
        res?.message ?? `${memberToDelete.name} has been removed successfully.`
      );
      setDeleteModalOpen(false);
      setMemberToDelete(null);
      fetchMembers();
    } catch (error: any) {
      console.error("Error deleting member:", error);
      notify.error(
        "",
        error?.response?.data?.message ??
          error?.message ??
          "Failed to delete member"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setMemberToDelete(null);
  };

  const totalCount = members.length;

  const columns: TableColumnsType<any> = useMemo(
    () => [
      {
        title: "Name",
        dataIndex: "clientName",
        key: "clientName",
        width: 200,
      },
      {
        title: "Member Code",
        dataIndex: "groupCode",
        key: "groupCode",
        width: 150,
      },
      {
        title: "Primary Member ID",
        dataIndex: "ipoUserID",
        key: "ipoUserID",
        width: 200,
      },
      {
        title: "Mobile Number",
        dataIndex: "mobile",
        key: "mobile",
        width: 150,
      },
      {
        title: "UPI ID",
        dataIndex: "upi",
        key: "upi",
        width: 200,
      },
      {
        title: "PAN Number",
        dataIndex: "pan",
        key: "pan",
        width: 150,
      },
      {
        title: "Dipository",
        dataIndex: "depository",
        key: "depository",
        width: 150,
      },
      {
        title: "DP ID",
        dataIndex: "dpId",
        key: "dpId",
        width: 200,
      },
      {
        title: "UPI Status",
        dataIndex: "upiVerified",
        showFilter: false,
        key: "upiVerified",
        width: 150,
        render: (_, record: any) => {
          const isVerified =
            record?.upiVerified === true ||
            String(record?.upiVerified).trim().toLowerCase() === "true";

          return (
            <Badge variant={isVerified ? "default" : "destructive"}>
              {isVerified ? "Verified" : "Unverified"}
            </Badge>
          );
        },
      },
      {
        title: "Joined Date",
        dataIndex: "created_on",
        key: "created_on",
        showFilter: false,
        width: 150,
        render: (date: string) => {
          if (!date) {
            return "-";
          }
          return new Date(date).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          });
        },
      },
      {
        title: "Actions",
        dataIndex: "actions",
        key: "actions",
        width: 100,
        showFilter: false,
        fixed: "right",
        render: (_, record: any) => (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-500"
              onClick={() =>
                navigate(`/admin/ipo/member-management/view/${record.id}`)
              }
            >
              <Eye className="h-4 w-4" />
            </Button>
            {/* <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-500"
              onClick={() =>
                navigate(`/admin/ipo/member-management/edit/${record.id}`)
              }
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-500"
              onClick={() =>
                handleDeleteClick(String(record.id), record.clientName)
              }
            >
              <Trash2 className="h-4 w-4" />
            </Button> */}
          </div>
        ),
      },
    ],
    []
  );

  const filteredData = members.filter(member => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (member?.clientName?.toLowerCase().includes(searchLower) ?? false) ||
      (member?.upi?.toLowerCase().includes(searchLower) ?? false) ||
      (member?.pan?.toLowerCase().includes(searchLower) ?? false) ||
      (member?.mobile?.toLowerCase().includes(searchLower) ?? false) ||
      (member?.groupCode?.toLowerCase().includes(searchLower) ?? false)
    );
  });

  const handleExportMembers = () => {
    const exportList = selectedRows.length > 0 ? selectedRows : [];

    if (exportList.length === 0) {
      notify.error("", "Please select rows to export.");
      return;
    }

    const rows = exportList.map(member => {
      const isVerified =
        member?.upiVerified === true ||
        String(member?.upiVerified).trim().toLowerCase() === "true";

      return {
        Name: member?.clientName ?? "",
        "Member Code": member?.groupCode ?? "",
        "Primary Member ID": member?.ipoUserID ?? "",
        "Mobile Number": member?.mobile ?? "",
        "UPI ID": member?.upi ?? "",
        "PAN Number": member?.pan ?? "",
        Depository: member?.depository ?? "",
        "DP ID": member?.dpId ?? "",
        "UPI Status": isVerified ? "Verified" : "Unverified",
        "Joined Date": member?.created_on
          ? new Date(member.created_on).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "",
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Members");

    const datePart = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `member_management_${datePart}.xlsx`);
    notify.success("", `${rows.length} member(s) exported successfully.`);
  };

  return (
    <div className="flex flex-col gap-6 p-8">
      {loading && <Loader message="Loading Members..." />}
      {/* Header */}
      <div className="relative mb-2 overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <Users className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                Member Management
              </h1>
              <p className="text-white/70">
                Manage team members, roles and permissions
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={syncMembers}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />{" "}
              Sync Data
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleExportMembers}
              disabled={selectedRows.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRows.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRows.length}
                </span>
              )}
            </Button>
            {/* <Button
              className="gap-2 bg-white text-secondary hover:bg-white/90"
              onClick={() => navigate("/admin/ipo/member-management/add")}
            >
              <Plus className="h-4 w-4" /> Add Member
            </Button> */}
          </div>
        </div>
      </div>

      {/* <Card className="rounded-2xl border bg-card shadow-sm">
        <CardContent className="p-6">
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search members..."
              className="pl-10"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card> */}

      <AntDTable
        data={filteredData}
        columns={columns}
        showColumnFilter
        rowKey="id"
        showSettings
        scroll={{ x: 1500 }}
        emptyText="No members found"
        className="ant-table-custom"
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows as GetMembers[]);
          },
          preserveSelectedRowKeys: false,
        }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} members`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
      />

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Member"
        message="Are you sure you want to delete"
        itemName={memberToDelete?.name ?? ""}
        isLoading={actionLoading}
        confirmText="Yes, Delete"
        cancelText="No"
      />
    </div>
  );
};

export default MemberManagement;
